'use client';

import { useLocale } from 'next-intl';
import { usePathname, useRouter } from '@/lib/navigation';
import { useAffiliate } from '@/components/affiliate/AffiliateProvider';
import { withRef } from '@/lib/ref-param';
import { useState, useTransition, useRef, useEffect } from 'react';
import { Globe, ChevronDown } from 'lucide-react';
import { PUBLIC_LOCALES } from '@/i18n';

// Labels for the current-locale trigger and the switch-to option list (the option
// lists iterate PUBLIC_LOCALES, which now includes nl as a fully promoted locale).
const localeNames: Record<string, string> = {
    en: 'English',
    de: 'Deutsch',
    fr: 'Français',
    hu: 'Magyar',
    nl: 'Nederlands',
};

/**
 * Desktop dropdown language switcher (used in desktop nav bars).
 */
export function LanguageSwitcher() {
    const locale = useLocale();
    const router = useRouter();
    const pathname = usePathname();
    const { refCode } = useAffiliate();
    const [isPending, startTransition] = useTransition();
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    const switchLocale = (newLocale: string) => {
        if (newLocale === locale) {
            setIsOpen(false);
            return;
        }

        startTransition(() => {
            router.replace(withRef(pathname, refCode), { locale: newLocale });
            setIsOpen(false);
        });
    };

    useEffect(() => {
        if (!isOpen || !dropdownRef.current) return;

        const dropdown = dropdownRef.current;
        const rect = dropdown.getBoundingClientRect();

        // eslint-disable-next-line no-undef
        if (rect.right > window.innerWidth) {
            dropdown.style.right = '0';
            dropdown.style.left = 'auto';
        }

        if (rect.left < 0) {
            dropdown.style.left = '0';
            dropdown.style.right = 'auto';
        }
    }, [isOpen]);

    return (
        <div className="relative">
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="flex items-center gap-2 text-trust-slate hover:text-eu-blue transition-colors font-medium"
                aria-label="Change language"
                disabled={isPending}>
                <Globe className="w-5 h-5" />
                <span>{localeNames[locale]}</span>
                <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
            </button>

            {isOpen && (
                <>
                    {/* Backdrop */}
                    <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />

                    {/* Dropdown */}
                    <div
                        ref={dropdownRef}
                        className="absolute right-0 mt-2 py-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
                        {PUBLIC_LOCALES.map((loc) => (
                            <button
                                key={loc}
                                onClick={() => switchLocale(loc)}
                                disabled={isPending}
                                className={`w-full text-left px-4 py-2 hover:bg-gray-50 transition-colors ${
                                    loc === locale ? 'bg-eu-blue/10 text-eu-blue font-semibold' : 'text-trust-slate'
                                }`}>
                                {localeNames[loc]}
                                {loc === locale && <span className="float-right text-eu-blue">✓</span>}
                            </button>
                        ))}
                    </div>
                </>
            )}
        </div>
    );
}

/**
 * Mobile inline accordion language switcher (used inside mobile menu panels).
 * Expands in-place without absolute positioning or backdrop overlays.
 */
export function MobileLanguageSwitcher() {
    const locale = useLocale();
    const router = useRouter();
    const pathname = usePathname();
    const { refCode } = useAffiliate();
    const [isPending, startTransition] = useTransition();
    const [isOpen, setIsOpen] = useState(false);

    const switchLocale = (newLocale: string) => {
        if (newLocale === locale) {
            setIsOpen(false);
            return;
        }

        startTransition(() => {
            router.replace(withRef(pathname, refCode), { locale: newLocale });
            setIsOpen(false);
        });
    };

    return (
        <div className="w-full">
            {/* Accordion trigger */}
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="flex items-center justify-between w-full text-base font-medium text-trust-slate hover:text-eu-blue py-3 px-4 rounded-lg hover:bg-eu-blue/5 transition-colors"
                aria-label="Change language"
                disabled={isPending}>
                <span className="flex items-center gap-2">
                    <Globe className="w-5 h-5" />
                    {localeNames[locale]}
                </span>
                <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
            </button>

            {/* Accordion content - inline expand */}
            <div
                className={`overflow-hidden transition-all duration-200 ease-out ${
                    isOpen ? 'max-h-48 opacity-100' : 'max-h-0 opacity-0'
                }`}>
                <div className="pl-6 pr-4 pb-1 flex flex-col gap-1">
                    {PUBLIC_LOCALES.map((loc) => (
                        <button
                            key={loc}
                            onClick={() => switchLocale(loc)}
                            disabled={isPending}
                            className={`w-full text-left py-2 px-4 rounded-lg text-sm transition-colors ${
                                loc === locale
                                    ? 'bg-eu-blue/10 text-eu-blue font-semibold'
                                    : 'text-trust-slate hover:bg-gray-50'
                            }`}>
                            {localeNames[loc]}
                            {loc === locale && <span className="float-right text-eu-blue">✓</span>}
                        </button>
                    ))}
                </div>
            </div>
        </div>
    );
}
